import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const FALLBACK = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=90';

export default function HeroSlideshow({
  images = [],
  title = 'The Sample House',
  subtitle = 'Premium Sample Solutions',
  description = 'Crafting excellence in every sample. Your trusted partner for premium textile sample solutions.',
  ctaButtons = [],
  interval = 5000,
}) {
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  const allImages = images.length > 0 ? images : [FALLBACK];
  const multi = allImages.length > 1;

  useEffect(() => {
    if (!multi) return;
    const timer = setInterval(() => {
      if (!paused.current) setIdx(i => (i + 1) % allImages.length);
    }, interval);
    return () => clearInterval(timer);
  }, [multi, allImages.length, interval]);

  const defaultButtons = [
    { label: 'Explore Products', link: '/products' },
    { label: 'Our Solutions', link: '/solutions' },
  ];
  const buttons = ctaButtons.length > 0 ? ctaButtons : defaultButtons;

  return (
    <section
      className="relative h-screen flex items-center justify-center overflow-hidden"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      {/* Slides */}
      <AnimatePresence initial={false}>
        <motion.div
          key={idx}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <img
            src={allImages[idx]}
            alt={`Hero ${idx + 1}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/55" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="text-xs tracking-[0.4em] uppercase mb-6 font-medium"
          style={{ color: 'var(--color-accent)' }}>
          {subtitle}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="text-5xl md:text-7xl font-serif leading-tight mb-6"
          style={{ color: '#F2EDE4' }}>
          {title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="text-lg mb-10 max-w-xl mx-auto"
          style={{ color: '#E5E0D8' }}>
          {description}
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center">
          {buttons.map((btn, i) => (
            <Link key={i} to={btn.link || '/products'}
              className={i === 0 ? 'btn-gold' : 'btn-outline'}>
              {btn.label}
            </Link>
          ))}
        </motion.div>
      </div>

      {/* Left / Right arrows */}
      {multi && (
        <>
          <button
            onClick={() => setIdx(i => (i - 1 + allImages.length) % allImages.length)}
            className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/30 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
            aria-label="Previous slide">
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={() => setIdx(i => (i + 1) % allImages.length)}
            className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/30 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200 hover:scale-110"
            aria-label="Next slide">
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {multi && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {allImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ background: i === idx ? '#D4AF37' : 'rgba(255,255,255,0.5)' }}
            />
          ))}
        </div>
      )}

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-px h-12 mx-auto" style={{ background: 'rgba(212,175,55,0.5)' }} />
      </div>
    </section>
  );
}
